import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getSent, getSentAttachments, type SentMessage, type SentAttachment } from '@/api/inbox';
import { API_BASE_URL } from '@/api/client';
import { Send, Paperclip, ArrowLeft, Search, X, Download } from 'lucide-react';
import { sanitizeEmailHtml } from '@/lib/sanitize';
import { SenderAvatar } from '@/components/sender-avatar';
import { cn } from '@/lib/utils';

function fmtDate(sec: number | null): string {
  if (!sec) return '';
  return new Date(sec * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function fmtFull(sec: number | null): string {
  if (!sec) return '';
  return new Date(sec * 1000).toLocaleString();
}

function fmtSize(bytes: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// Download via fetch + blob so the httpOnly auth cookie travels with the request
// (same pattern as message-view.tsx — plain <a href> fails in the Capacitor shell).
async function downloadSentAttachment(sentId: string, att: SentAttachment) {
  try {
    const res = await fetch(`${API_BASE_URL}/inbox/sent/${encodeURIComponent(sentId)}/attachments/${encodeURIComponent(att.id)}`, {
      credentials: 'include'
    });
    if (!res.ok) {
      const message = await res.json().then((b) => b?.error, () => null);
      if (res.status === 404 && message) { alert(message); return; }
      throw new Error(`Download failed (${res.status})`);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = att.filename || 'attachment';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  } catch (err) {
    console.error('sent attachment download failed:', err);
    alert("Couldn't download attachment — try signing out and back in, then retry.");
  }
}

/** Attachment chips for one sent message. Loaded lazily — only when the row says there are any. */
function SentAttachments({ sentId }: { sentId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['sent-attachments', sentId],
    queryFn: () => getSentAttachments(sentId)
  });
  const atts = data?.attachments || [];
  if (isLoading) {
    return null;
  }
  if (!atts.length) {
    return <p className="mt-3 text-xs text-bmail-text-secondary">Attachment not stored — it predates attachment capture or only the original mail client kept a copy.</p>;
  }
  return (
    <div className="mt-3 flex flex-wrap gap-2 border-t border-bmail-border pt-3">
      {atts.map((a) => (
        <button
          key={a.id}
          type="button"
          onClick={() => downloadSentAttachment(sentId, a)}
          className="flex items-center gap-2 rounded-lg border border-bmail-border bg-bmail-surface px-3 py-2 text-xs text-bmail-text hover:bg-bmail-surface-hover transition-colors"
          title={`Download ${a.filename || 'attachment'}`}
        >
          <Download size={14} className="shrink-0 text-bmail-text-secondary" />
          <span className="max-w-[180px] truncate">{a.filename || 'attachment'}</span>
          <span className="text-bmail-text-secondary">({fmtSize(a.size)})</span>
        </button>
      ))}
    </div>
  );
}

/**
 * Expanded body for one sent message.
 *
 * Renders straight from the row we already hold — `sent_messages` stores body_text and
 * body_html for BOTH b-mail's own sends and the ones pulled from the account's real Sent
 * folder, so opening a message needs no extra request and no IMAP round-trip.
 *
 * HTML goes through the same `sanitizeEmailHtml` the inbox uses. It is the user's own sent
 * mail, but "own" is not "safe": a reply quotes whatever the other party sent, so the HTML
 * can still carry anything they put in it.
 */
function SentBody({ m }: { m: SentMessage }) {
  const html = m.bodyHtml ? sanitizeEmailHtml(m.bodyHtml) : null;
  return (
    <div className="mt-3 rounded-lg border border-bmail-border bg-bmail-surface p-3">
      <div className="mb-2 grid gap-1 border-b border-bmail-border pb-2 text-xs text-bmail-text-secondary">
        <div><span className="font-medium">From:</span> {m.fromEmail || m.mailboxEmail}</div>
        <div><span className="font-medium">To:</span> {m.to.length ? m.to.join(', ') : '(no recipient)'}</div>
        <div><span className="font-medium">Sent:</span> {fmtFull(m.sentAt)}</div>
      </div>
      {html ? (
        // B-22: contain:paint keeps quoted mail's position:fixed inside this box (see message-view).
        <div className="max-w-full overflow-x-auto [&_table]:max-w-full [&_table]:overflow-x-auto [&_img]:max-w-full" style={{ contain: 'paint', position: 'relative' }}>
          <div
            className="prose prose-invert max-w-none text-sm text-bmail-text [&_a]:text-bmail-gold [&_pre]:whitespace-pre-wrap [&_pre]:break-words"
            style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </div>
      ) : m.bodyText ? (
        <pre className="whitespace-pre-wrap break-words font-sans text-sm text-bmail-text">{m.bodyText}</pre>
      ) : (
        <p className="text-sm text-bmail-text-secondary">This message has no stored body.</p>
      )}
      {m.hasAttachments && <SentAttachments sentId={m.id} />}
    </div>
  );
}

export function SentRoute({ mailboxId }: { mailboxId?: string } = {}) {
  const { data, isLoading } = useQuery({ queryKey: ['sent', mailboxId ?? 'all'], queryFn: () => getSent(mailboxId) });
  const messages = data?.messages || [];
  const [openId, setOpenId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Client-side filter — Sent is a small, fully-loaded list (unlike INBOX which is
  // paginated server-side), so we filter the rows we already hold. Matches on
  // recipient, sender, subject, snippet, and body text, case-insensitive.
  const q = searchQuery.trim().toLowerCase();
  const visible = q
    ? messages.filter((m) =>
        [m.subject || '', m.snippet || '', m.bodyText || '', m.fromEmail || '', m.mailboxEmail || '', ...m.to]
          .join(' ')
          .toLowerCase()
          .includes(q))
    : messages;
  const selected = messages.find((m) => m.id === openId) || null;

  return (
    <div className="flex h-full w-full">
      {/* Message list — full width on mobile when nothing selected, 1/3 on desktop */}
      <div className={`
        flex flex-col overflow-y-auto border-r border-bmail-border bg-bmail-surface
        ${openId ? 'hidden md:flex md:w-1/3 md:min-w-[20rem] md:max-w-md' : 'flex w-full md:w-1/3 md:min-w-[20rem] md:max-w-md'}
      `}>
        <div className="border-b border-bmail-border px-4 py-4 md:px-6">
          <h1 className="flex items-center gap-2 text-xl font-bold text-bmail-text">
            <Send size={20} /> Sent
          </h1>
          <p className="mt-1 text-sm text-bmail-text-secondary">
            {mailboxId ? 'Sent from this mailbox, including other mail apps.' : 'Sent across all your mailboxes, including other mail apps.'}
          </p>
        </div>

        {/* Search bar — same pattern as the inbox list, but client-side since Sent is fully loaded */}
        <div className="border-b border-bmail-border px-4 py-2 md:px-6">
          <div className="flex items-center gap-2 rounded-md border border-bmail-border bg-[var(--bg)] px-2 py-1.5">
            <Search size={14} className="shrink-0 text-bmail-text-secondary" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={mailboxId ? 'Search sent from this mailbox…' : 'Search sent across all mailboxes…'}
              className="w-full bg-transparent text-sm text-bmail-text outline-none placeholder:text-bmail-text-secondary"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                title="Clear search"
                className="shrink-0 text-bmail-text-secondary hover:text-bmail-text"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {isLoading ? (
          null
        ) : visible.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center p-10 text-center text-bmail-text-secondary">
            {q ? (
              <>
                <Search size={40} className="mb-3 opacity-40" />
                <p className="text-sm">No sent messages match “{searchQuery.trim()}”.</p>
              </>
            ) : (
              <>
                <Send size={40} className="mb-3 opacity-40" />
                <p className="text-sm">Nothing sent yet. Messages you send will show up here.</p>
              </>
            )}
          </div>
        ) : (
          <div className="mx-auto w-full max-w-3xl">
          {visible.map((m) => {
            const toLabel = m.to.length ? m.to.join(', ') : '(no recipient)';
            return (
              <div
                key={m.id}
                className={cn(
                  'flex items-center border-b border-bmail-border px-2 transition-colors sm:px-3',
                  openId === m.id ? 'bg-bmail-surface-hover' : 'bg-transparent hover:bg-bmail-surface-hover'
                )}
              >
                <SenderAvatar name={toLabel} email={m.to[0] || m.fromEmail} size={36} />
                <button
                  type="button"
                  onClick={() => setOpenId(openId === m.id ? null : m.id)}
                  className="min-w-0 flex-1 px-3 py-3 text-left"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex min-w-0 items-center gap-1.5 truncate text-[15px] text-bmail-text">
                      <span className="text-bmail-text-secondary">To: </span>
                      {toLabel}
                    </span>
                    <span className="shrink-0 text-xs text-bmail-text-secondary">{fmtDate(m.sentAt)}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-[14px] text-bmail-text">{m.subject || '(no subject)'}</span>
                    {m.hasAttachments && <Paperclip size={14} className="shrink-0 text-bmail-text-secondary" />}
                  </div>
                  {m.snippet && <p className="mt-1 line-clamp-1 text-[13px] text-bmail-text-secondary">{m.snippet}</p>}
                </button>
              </div>
            );
          })}
          </div>
        )}
      </div>

      {/* Reader pane — full width on mobile when selected, 2/3 on desktop */}
      <div className={`
        flex flex-col bg-[var(--bg)]
        ${openId ? 'flex w-full md:flex-1' : 'hidden md:flex md:flex-1'}
      `}>
        {selected ? (
          <>
            <button
              onClick={() => setOpenId(null)}
              className="flex items-center gap-1 border-b border-bmail-border px-4 py-2 text-sm text-bmail-text-secondary hover:text-bmail-text md:hidden"
            >
              <ArrowLeft size={16} /> Back to sent
            </button>
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 md:px-6">
              <SentBody m={selected} />
            </div>
          </>
        ) : (
          <div className="flex h-full items-center justify-center text-bmail-text-secondary">
            <div className="text-center">
              <Send size={40} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm">Select a sent message to read it</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
